import beCreateAppointment from 'actions/appointment/beCreateAppointment.action';
import beUpdateAppointment from 'actions/appointment/beUpdateAppointment.action';
import LINK from 'constants/link.const';
import asArray from 'utils/arrays/asArray.util';
import { fromPropsToIsWeek, fromPropsToMatchedParams, fromPropsToPathSegments } from 'utils/converters/props/path.util';
import { fromAnyDateToZulu } from 'utils/converters/time/from.util';
import { formatLocalizedTime, formatYearToDayStandard } from 'utils/date/format.util';
import call from 'utils/functions/call.util';
import linkTo from 'utils/links/linkTo.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';
import { concatTag as ct } from 'utils/strings/manipulate.util';

const FTAG = 'saveBooking';
const L = logger(FTAG);

const fromServiceToProcedure = (s) => ({
  stylist_id: s?.stylist_id,
  service_id: s?.service_id,
  price: s?.price,
  duration: s?.duration,
  processing: s?.processing,
  is_custom_price: s?.is_custom_price,
  is_custom_duration: s?.is_custom_duration,
  is_custom_processing: s?.is_custom_processing,
  start_time: fromAnyDateToZulu(s.start_time),
  id: s.id > 0 ? s.id : void 1,
});

const fromServiceToProcedureWithAppointment = (s) => {
  const p = fromServiceToProcedure(s);
  p.appointment = { ...s.appointment };
  return p;
};

const fromServicesToProcedures = (services) =>
  asArray(services)
    .filter(({ deleted }) => !deleted)
    .map(fromServiceToProcedure);

const fromServicesToProceduresWithAppointments = (services) =>
  asArray(services)
    .filter(({ deleted }) => !deleted)
    .map(fromServiceToProcedureWithAppointment);

const isTimeNotSet = (services) =>
  asArray(services).every((s) => {
    if (s?.deleted === true) return false;
    if (!s.stylist_id) return true;

    const bgn = formatLocalizedTime(s?.start_time);
    if (bgn === '12:00 AM') return true;

    return 0 !== parseInt(bgn.split(' ')[0].split(':')[1], 10) % 15;
  });

const R = Object.freeze({
  zero: '#0a: edit first repeat booking',
  oneA: '#1a: edit appointment, add deposit, w/ POS',
  oneB: '#1b: edit appointment, add deposit, no POS',
  two: '#2: edit appointment, no deposit',
  threeA: '#3a: edit repeat appointment, no deposit',
  threeB: '#3b: edit repeat appointment, no deposit, no updater',
  fourA: '#4a: new repeat appointment, no deposit, no time set',
  fourB: '#4b: new repeat appointment, no deposit',
  fourC: '#4c: new appointment, with deposit, no time set',
  fourD: '#4d: new appointment, with deposit, w/ POS',
  fourE: '#4e: new appointment, with deposit, no POS',
  fiveA: '#5a: new appointment, no deposit, no time set',
  fiveB: '#5b: new appointment, no deposit',
});

/*
 * if it's repeat booking, the save will show a confirmation popup
 * asking whether the change is for one or for all.
 *
 * This call of save will have oneOrAll as a string 'one' or 'all'
 * and after the user select one or all,
 * the save is called again without oneOrAll,
 * this time the typeof oneOrAll will be an object
 */
const saveBooking = (options) => {
  L.debug('saveBooking()', options);
  options = options ?? {};

  const oneOrAll = options.oneOrAll;
  const onChange = (tag, ev) => call(ct('saveBooking.onStateChange', tag), options.onStateChange, ev);
  const onClear = (tag, ev) => call(ct('saveBooking.cleanBookingData', tag), options.cleanBookingData, ev);

  const { frequencyType, isRepeatBooking } = options;

  if (isRepeatBooking && oneOrAll !== 'all' && oneOrAll !== 'one' && frequencyType !== 'never_repeat') {
    // notify user that he is only editing the first repeat booking
    onChange(R.zero, { dialogOpened: true });

    if (typeof oneOrAll === 'object') {
      L.info(`Saving booking condition ${R.zero} ...`);
      return R.zero;
    }

    // NOTE: no return here, we continue, because of the comment above this function
  }

  const { stylistId } = fromPropsToMatchedParams(options);
  const isWeek = fromPropsToIsWeek(options);
  const segments = fromPropsToPathSegments(options);

  const { addedServices, salonId } = options;
  const appointmentId = addedServices?.[0]?.appointment?.id;

  const navToScheduler = (...tags) => {
    const params = { salonId, dateString: segments[4], stylistId };
    const pattern = isWeek ? LINK.scheduleWeek : LINK.scheduleDay;
    return navFor(FTAG, ...tags, { isWeek, salonId }, options).push(linkTo({ pattern, params }));
  };

  const { appointmentDetails, bookingNotes, clientId, deposit } = options;
  const { beUpdateAppointment: beUpdateAppointmentFromOptions, updateAppointmentAndDepositThunk } = options;

  if (appointmentId && deposit) {
    const data = { client_id: clientId, procedures: fromServicesToProcedures(addedServices), notes: bookingNotes };

    if (deposit.slickPosReader) {
      L.info(`Saving booking condition ${R.oneA} ...`);

      const promise = beUpdateAppointmentFromOptions({ salonId, appointmentId, data, appointmentDetails });
      L.unignore(FTAG, 'beUpdateAppointment:', promise);

      promise.then((result) => {
        if (result.type === beUpdateAppointment.AT.pass) {
          onChange(R.oneA, { openPosModal: true, appointmentId: result.json.id });
        }
      });

      return R.oneA;
    }

    L.info(`Saving booking condition ${R.oneB}...`);

    const promise = updateAppointmentAndDepositThunk(salonId, appointmentId, data, deposit);
    L.unignore(FTAG, 'updateAppointmentAndDepositThunk:', promise);

    onClear(R.oneB);
    navToScheduler(R.oneB);
    return R.oneB;
  }

  const { updateAppointmentWithProcedures } = options;

  const procedures = fromServicesToProceduresWithAppointments(addedServices);

  if (appointmentId && !deposit && (!isRepeatBooking || (isRepeatBooking && oneOrAll === 'one'))) {
    L.info(`Saving booking condition ${R.two}...`);

    const data = { client_id: clientId, procedures, notes: bookingNotes };
    const promise = updateAppointmentWithProcedures(salonId, appointmentId, data);
    L.unignore(FTAG, 'updateAppointmentWithProcedures:', promise);

    onClear(R.two);
    navToScheduler(R.two);
    return R.two;
  }

  const { frequency, isEditBooking, until, updateAppointmentAndRepeatThunk } = options;

  if (isRepeatBooking && isEditBooking && (oneOrAll === 'all' || typeof oneOrAll === 'object')) {
    // FIXME: this entire flow is currently not working since all oneOrAll are set to one.

    if (!updateAppointmentAndRepeatThunk) {
      L.info(`Saving booking condition ${R.threeB}...`);
      return R.threeB;
    }

    L.info(`Saving booking condition ${R.threeA}...`);

    const rules = { frequency_type: frequencyType, frequency, until: formatYearToDayStandard(until) };
    if (frequencyType === 'never_repeat') rules.frequency_type = 'weeks';

    const promise = updateAppointmentAndRepeatThunk(salonId, procedures, rules);
    L.unignore(FTAG, 'updateAppointmentAndRepeatThunk()', promise);

    onClear(R.threeA);
    navToScheduler(R.threeA);
    return R.threeA;
  }

  const { appointmentAndRepeatThunk } = options;

  if (isRepeatBooking && !isEditBooking) {
    if (isTimeNotSet(addedServices)) {
      L.info(`Saving booking condition ${R.fourA}...`);

      onChange(R.fourA, { confirmDialogOpen: true });
      return R.fourA;
    }

    L.info(`Saving booking condition ${R.fourB}...`);

    const data = {
      client_id: clientId,
      procedures,
      notes: bookingNotes,
      frequency_type: frequencyType,
      frequency,
      until: formatYearToDayStandard(until),
    };

    const promise = appointmentAndRepeatThunk(salonId, data);
    L.unignore(FTAG, 'appointmentAndRepeatThunk:', promise);

    onClear(R.fourB);
    navToScheduler(R.fourB);
    return R.fourB;
  }

  const { appointmentAndDepositThunk, beCreateAppointment: beCreateAppointmentFromOptions } = options;

  if (deposit) {
    if (isTimeNotSet(addedServices)) {
      L.info(`Saving booking condition ${R.fourC}...`);

      onChange(R.fourC, { confirmDialogOpen: true });
      return R.fourC;
    }

    const data = {
      client_id: clientId,
      procedures,
      notes: bookingNotes,
    };

    if (deposit.slickPosReader) {
      L.info(`Saving booking condition ${R.fourD}...`);

      const promise = beCreateAppointmentFromOptions({ salonId, data, deposit });
      L.unignore(FTAG, 'beCreateAppointment:', promise);

      promise.then((result) => {
        if (result.type !== beCreateAppointment.AT.pass) return;
        onChange(R.fourD, { openPosModal: true, appointmentId: result.json.id });
      });

      return R.fourD;
    }

    L.info(`Saving booking condition ${R.fourE}...`);

    const promise = appointmentAndDepositThunk(salonId, data, deposit);
    L.unignore(FTAG, 'appointmentAndDepositThunk:', promise);

    onClear(R.fourE);
    navToScheduler(R.fourE);
    return R.fourE;
  }

  const { createAppointmentWithProcedures } = options;

  if (isTimeNotSet(addedServices)) {
    L.info(`Saving booking condition ${R.fiveA} ...`);
    onChange(R.fiveA, { confirmDialogOpen: true });
    return R.fiveA;
  }

  L.info(`Saving booking condition ${R.fiveB} ...`);

  const data = {
    client_id: (addedServices?.[0]?.appointment?.client ?? null) === 'walkIn' ? null : clientId || null,
    procedures,
    notes: bookingNotes,
  };

  const promise = createAppointmentWithProcedures(salonId, data);
  L.unignore(FTAG, 'createAppointmentWithProcedures:', promise);

  onClear(R.fiveB);
  navToScheduler(R.fiveB);
  return R.fiveB;
};

export default saveBooking;
